import React, { useState, useCallback, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import ReactFlow, {
  Node,
  Edge,
  addEdge,
  Connection,
  useNodesState,
  useEdgesState,
  Controls,
  Background,
  MiniMap,
  ReactFlowProvider,
  MarkerType,
  Position,
  Handle
} from 'reactflow';
import 'reactflow/dist/style.css';
import {
  Sparkles,
  Mail,
  MessageSquare,
  Clock,
  Users,
  Target,
  BarChart3,
  Settings,
  Save,
  Play,
  Pause,
  Copy,
  Trash2,
  Plus,
  Wand2,
  Brain,
  Zap,
  Send,
  Calendar,
  Filter,
  Tag,
  Bell,
  Phone,
  Building,
  Globe,
  Star,
  Heart,
  AlertTriangle,
  CheckCircle,
  ArrowRight,
  MoreHorizontal,
  Edit3,
  Eye,
  Download,
  Share2,
  Maximize2,
  X,
  Search,
  ChevronDown,
  Upload,
  User,
  Palette,
  Award
} from 'lucide-react';
import { Button } from '../components/ui/Button/Button';
import { cn } from '../utils/cn';
import GeminiService, { SequenceRequest, TemplateRequest, GeneratedTemplate } from '../services/geminiService';
import { PersonalizationModal } from '../components/PersonalizationModal';
import { TemplateGallery } from '../components/TemplateGallery';
import TemplateService, { PersonalizationData, HTMLTemplate } from '../services/templateService';

// Template interface for existing templates
interface Template {
  id: string;
  name: string;
  category: string;
  type: 'email' | 'sms' | 'linkedin' | 'call';
  subject?: string;
  content: string;
  tags: string[];
  openRate?: number;
  replyRate?: number;
  usage: number;
  createdAt: string;
  updatedAt: string;
  isActive: boolean;
  author: string;
}

// Sequence interface for saving
interface Sequence {
  id: string;
  name: string;
  description: string;
  nodes: Node[];
  edges: Edge[];
  status: 'draft' | 'active' | 'paused';
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  estimatedDuration: string;
  expectedResults: {
    estimatedReach: number;
    expectedOpenRate: number;
    expectedReplyRate: number;
    estimatedMeetings: number;
  };
}

// Modal props for template selection/creation
interface TemplateModalProps {
  isOpen: boolean;
  onClose: () => void;
  nodeType: 'email' | 'sms' | 'linkedin' | 'call';
  existingTemplates: Template[];
  onSelectTemplate: (template: Template) => void;
  onCreateTemplate: (template: GeneratedTemplate) => void;
  selectedNode?: Node | null;
}

// Enhanced Template Selection Modal Component
const TemplateSelectionModal: React.FC<TemplateModalProps> = ({
  isOpen,
  onClose,
  nodeType,
  existingTemplates,
  onSelectTemplate,
  onCreateTemplate,
  selectedNode
}) => {
  const [activeTab, setActiveTab] = useState<'existing' | 'create' | 'beautiful'>('beautiful');
  const [searchQuery, setSearchQuery] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [aiPrompt, setAiPrompt] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('cold-outreach');
  const [personalizationLevel, setPersonalizationLevel] = useState<'basic' | 'advanced' | 'ai-powered'>('ai-powered');
  const [showPersonalizationModal, setShowPersonalizationModal] = useState(false);
  const [showTemplateGallery, setShowTemplateGallery] = useState(false);
  const [recipientData, setRecipientData] = useState<PersonalizationData | undefined>();

  const filteredTemplates = existingTemplates.filter(template => 
    template.type === nodeType &&
    template.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handlePersonalizationSave = (data: PersonalizationData) => {
    setRecipientData(data);
    setShowPersonalizationModal(false);
    setShowTemplateGallery(true);
  };

  const handleTemplateSelection = (template: HTMLTemplate, personalizedHTML?: string) => {
    // Convert HTMLTemplate to our existing Template format
    const convertedTemplate = {
      id: template.id,
      name: template.name,
      category: template.category,
      type: nodeType,
      subject: personalizedHTML ? extractSubjectFromHTML(personalizedHTML) : `Beautiful ${template.name}`,
      content: personalizedHTML || template.description,
      tags: template.features,
      openRate: 28.5, // Mock performance data
      replyRate: 8.2,
      usage: 0,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      isActive: true,
      author: 'Beautiful Templates'
    };

    onSelectTemplate(convertedTemplate);
    onClose();
  };

  const extractSubjectFromHTML = (html: string): string => {
    const titleMatch = html.match(/<title>(.*?)<\/title>/i);
    if (titleMatch) return titleMatch[1];
    
    const h1Match = html.match(/<h1[^>]*>(.*?)<\/h1>/i);
    if (h1Match) return h1Match[1].replace(/<[^>]*>/g, '');
    
    return 'Personalized Email';
  };

  const generateTemplate = async () => {
    if (!aiPrompt.trim()) return;

    setIsGenerating(true);
    try {
      const request: TemplateRequest = {
        type: nodeType,
        category: selectedCategory,
        description: aiPrompt,
        personalizationLevel,
        targetAudience: 'B2B professionals',
        objective: 'Generate engagement and responses'
      };

      const generatedTemplate = await GeminiService.generateTemplate(request);
      onCreateTemplate(generatedTemplate);
      onClose();
    } catch (error) {
      console.error('Template generation failed:', error);
    } finally {
      setIsGenerating(false);
    }
  };

  const getNodeTypeInfo = (type: string) => {
    switch (type) {
      case 'email': return { icon: Mail, color: 'text-blue-400', bg: 'bg-blue-500/20' };
      case 'sms': return { icon: MessageSquare, color: 'text-green-400', bg: 'bg-green-500/20' };
      case 'linkedin': return { icon: Building, color: 'text-purple-400', bg: 'bg-purple-500/20' };
      case 'call': return { icon: Phone, color: 'text-red-400', bg: 'bg-red-500/20' };
      default: return { icon: Mail, color: 'text-blue-400', bg: 'bg-blue-500/20' };
    }
  };

  const nodeInfo = getNodeTypeInfo(nodeType);

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className="bg-gradient-to-br from-neutral-900/95 to-neutral-800/95 backdrop-blur-xl border border-neutral-700/50 rounded-2xl w-full max-w-5xl max-h-[90vh] overflow-hidden shadow-2xl"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-neutral-700/50">
            <div className="flex items-center space-x-3">
              <div className={cn("w-10 h-10 rounded-xl flex items-center justify-center", nodeInfo.bg)}>
                <nodeInfo.icon className={cn("w-5 h-5", nodeInfo.color)} />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">
                  {nodeType.charAt(0).toUpperCase() + nodeType.slice(1)} Template
                </h2>
                <p className="text-neutral-400 text-sm">Choose from beautiful templates or create with AI</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-neutral-400 hover:text-white hover:bg-neutral-700/50 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Enhanced Tabs */}
          <div className="flex items-center space-x-1 bg-neutral-800/50 rounded-xl p-1 m-6 mb-0">
            {[
              { id: 'beautiful', label: 'Beautiful Templates', icon: Sparkles, description: 'Professional HTML designs' },
              { id: 'existing', label: 'Existing Templates', count: filteredTemplates.length },
              { id: 'create', label: 'Create with AI', icon: Brain }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={cn(
                  'flex items-center space-x-2 px-4 py-3 text-sm font-medium rounded-lg transition-all duration-200',
                  activeTab === tab.id
                    ? 'bg-blue-500 text-white shadow-lg'
                    : 'text-neutral-400 hover:text-white hover:bg-neutral-700/50'
                )}
              >
                {tab.icon && <tab.icon className="w-4 h-4" />}
                <span>{tab.label}</span>
                {tab.count !== undefined && (
                  <span className="ml-2 text-xs bg-neutral-600 text-neutral-300 px-1.5 py-0.5 rounded-full">
                    {tab.count}
                  </span>
                )}
              </button>
            ))}
          </div>

          <div className="p-6 max-h-[60vh] overflow-y-auto">
            {/* Beautiful Templates Tab */}
            {activeTab === 'beautiful' && (
              <div className="space-y-6">
                <div className="text-center">
                  <div className="w-20 h-20 bg-gradient-to-r from-purple-500 to-pink-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <Sparkles className="w-10 h-10 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-3">
                    Beautiful Email Templates
                  </h3>
                  <p className="text-neutral-400 mb-6 max-w-2xl mx-auto">
                    Choose from our collection of professionally designed, mobile-responsive email templates. 
                    Each template can be personalized with AI for maximum impact.
                  </p>
                  
                  <div className="flex items-center justify-center space-x-4 mb-8">
                    <Button
                      onClick={() => setShowPersonalizationModal(true)}
                      className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600"
                    >
                      <User className="w-4 h-4 mr-2" />
                      Add Recipient Info
                    </Button>
                    <Button
                      onClick={() => setShowTemplateGallery(true)}
                      variant="outline"
                      className="border-blue-500/50 text-blue-400"
                    >
                      <Palette className="w-4 h-4 mr-2" />
                      Browse Templates
                    </Button>
                  </div>
                  
                  {recipientData && (
                    <div className="bg-gradient-to-r from-green-500/10 to-emerald-500/10 border border-green-500/20 rounded-xl p-4 mb-6">
                      <div className="flex items-center space-x-2 mb-2">
                        <CheckCircle className="w-5 h-5 text-green-400" />
                        <span className="text-green-400 font-medium">Personalization Ready</span>
                      </div>
                      <p className="text-neutral-300 text-sm">
                        Templates will be personalized for <strong>{recipientData.firstName} {recipientData.lastName}</strong> 
                        {recipientData.company && ` from ${recipientData.company}`}
                      </p>
                    </div>
                  )}
                </div>

                {/* Template Categories Preview */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {[
                    { 
                      name: 'Sales Outreach', 
                      category: 'sales',
                      description: 'High-converting sales templates',
                      color: 'from-blue-500 to-cyan-500',
                      icon: Target 
                    },
                    { 
                      name: 'Follow-up', 
                      category: 'followup',
                      description: 'Professional follow-up designs',
                      color: 'from-green-500 to-emerald-500',
                      icon: ArrowRight 
                    },
                    { 
                      name: 'Meeting Booking', 
                      category: 'calendar',
                      description: 'Calendar integration templates',
                      color: 'from-purple-500 to-pink-500',
                      icon: Calendar 
                    },
                    { 
                      name: 'Professional', 
                      category: 'professional',
                      description: 'Enterprise-level designs',
                      color: 'from-orange-500 to-red-500',
                      icon: Award 
                    }
                  ].map((category, index) => (
                    <motion.div
                      key={category.category}
                      whileHover={{ scale: 1.05 }}
                      className="p-4 bg-neutral-800/30 border border-neutral-700/50 rounded-xl hover:border-neutral-600/50 transition-all duration-200 cursor-pointer"
                      onClick={() => {
                        // Filter to specific category when opening gallery
                        setShowTemplateGallery(true);
                      }}
                    >
                      <div className={cn(
                        "w-12 h-12 rounded-xl flex items-center justify-center mb-3 bg-gradient-to-r",
                        category.color
                      )}>
                        <category.icon className="w-6 h-6 text-white" />
                      </div>
                      <h4 className="font-semibold text-white text-sm mb-2">{category.name}</h4>
                      <p className="text-neutral-400 text-xs">{category.description}</p>
                    </motion.div>
                  ))}
                </div>
              </div>
            )}

            {/* Existing Templates Tab - keep existing code */}
            {activeTab === 'existing' && (
              <div className="space-y-4">
                {/* Search */}
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-neutral-400" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder={`Search ${nodeType} templates...`}
                    className="w-full pl-10 pr-4 py-3 bg-neutral-800/50 border border-neutral-700 rounded-xl text-white placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50"
                  />
                </div>

                {/* Templates Grid */}
                {filteredTemplates.length === 0 ? (
                  <div className="text-center py-12">
                    <nodeInfo.icon className="w-16 h-16 text-neutral-500 mx-auto mb-4" />
                    <h3 className="text-xl font-semibold text-white mb-2">
                      No {nodeType} templates found
                    </h3>
                    <p className="text-neutral-400 mb-6">
                      {searchQuery ? 'Try adjusting your search' : `Create your first ${nodeType} template`}
                    </p>
                    <Button
                      onClick={() => setActiveTab('create')}
                      className="bg-gradient-to-r from-blue-500 to-purple-600"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Create Template
                    </Button>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {filteredTemplates.map((template) => (
                      <motion.div
                        key={template.id}
                        whileHover={{ scale: 1.02 }}
                        className="p-4 bg-neutral-800/30 border border-neutral-700/50 rounded-xl hover:border-neutral-600/50 transition-all duration-200 cursor-pointer"
                        onClick={() => {
                          onSelectTemplate(template);
                          onClose();
                        }}
                      >
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <h3 className="font-semibold text-white text-sm">{template.name}</h3>
                            <div className="flex items-center space-x-2 mt-1">
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-500/20 text-blue-400">
                                {template.category.replace('-', ' ')}
                              </span>
                              {template.isActive && (
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-500/20 text-green-400">
                                  Active
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="text-blue-400 text-sm font-medium">{template.usage} uses</div>
                          </div>
                        </div>

                        {template.subject && (
                          <div className="mb-2">
                            <span className="text-neutral-400 text-xs">Subject:</span>
                            <p className="text-white text-sm font-medium truncate">{template.subject}</p>
                          </div>
                        )}

                        <div className="mb-3">
                          <span className="text-neutral-400 text-xs">Content:</span>
                          <p className="text-neutral-300 text-xs mt-1 line-clamp-2">{template.content}</p>
                        </div>

                        {(template.openRate || template.replyRate) && (
                          <div className="grid grid-cols-2 gap-3 p-2 bg-neutral-700/30 rounded-lg">
                            {template.openRate && (
                              <div className="text-center">
                                <div className="text-green-400 font-medium text-sm">{template.openRate}%</div>
                                <div className="text-neutral-400 text-xs">Open Rate</div>
                              </div>
                            )}
                            {template.replyRate && (
                              <div className="text-center">
                                <div className="text-blue-400 font-medium text-sm">{template.replyRate}%</div>
                                <div className="text-neutral-400 text-xs">Reply Rate</div>
                              </div>
                            )}
                          </div>
                        )}
                      </motion.div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Create with AI Tab - keep existing code */}
            {activeTab === 'create' && (
              <div className="space-y-6">
                {/* AI Generator */}
                <div className="bg-gradient-to-r from-purple-500/10 to-pink-500/10 border border-purple-500/20 rounded-xl p-6">
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="w-8 h-8 bg-gradient-to-r from-purple-500 to-pink-500 rounded-lg flex items-center justify-center">
                      <Brain className="w-4 h-4 text-white" />
                    </div>
                    <h3 className="text-lg font-semibold text-white">AI Template Generator</h3>
                  </div>
                  
                  <div className="space-y-4">
                    <textarea
                      value={aiPrompt}
                      onChange={(e) => setAiPrompt(e.target.value)}
                      placeholder={`Describe the ${nodeType} template you want to create. For example: 'Create a follow-up email for prospects who attended our webinar. Focus on the key takeaways and offer a personalized demo.'`}
                      rows={4}
                      className="w-full px-4 py-3 bg-neutral-800/50 border border-neutral-700 rounded-xl text-white placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-purple-500/50 resize-none"
                    />
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-neutral-300 mb-2">Category</label>
                        <select
                          value={selectedCategory}
                          onChange={(e) => setSelectedCategory(e.target.value)}
                          className="w-full px-3 py-2 bg-neutral-800/50 border border-neutral-700 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-purple-500/50"
                        >
                          <option value="cold-outreach">Cold Outreach</option>
                          <option value="follow-up">Follow-up</option>
                          <option value="meeting-request">Meeting Request</option>
                          <option value="proposal">Proposal</option>
                          <option value="thank-you">Thank You</option>
                        </select>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-neutral-300 mb-2">Personalization Level</label>
                        <select
                          value={personalizationLevel}
                          onChange={(e) => setPersonalizationLevel(e.target.value as any)}
                          className="w-full px-3 py-2 bg-neutral-800/50 border border-neutral-700 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-purple-500/50"
                        >
                          <option value="basic">Basic</option>
                          <option value="advanced">Advanced</option>
                          <option value="ai-powered">AI-Powered</option>
                        </select>
                      </div>
                    </div>
                    
                    <Button
                      onClick={generateTemplate}
                      disabled={!aiPrompt.trim() || isGenerating}
                      className="w-full bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600"
                    >
                      {isGenerating ? (
                        <>
                          <Sparkles className="w-4 h-4 mr-2 animate-spin" />
                          Generating Template...
                        </>
                      ) : (
                        <>
                          <Wand2 className="w-4 h-4 mr-2" />
                          Generate {nodeType.charAt(0).toUpperCase() + nodeType.slice(1)} Template
                        </>
                      )}
                    </Button>
                  </div>
                </div>

                {/* Quick Templates */}
                <div>
                  <h3 className="text-lg font-semibold text-white mb-4">Quick Templates</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {[
                      { name: 'Cold Outreach', desc: 'First contact template with value prop' },
                      { name: 'Follow-up', desc: 'Second touchpoint for non-responders' },
                      { name: 'Meeting Request', desc: 'Direct meeting scheduling template' },
                      { name: 'Thank You', desc: 'Appreciation and next steps' }
                    ].map((quickTemplate, index) => (
                      <button
                        key={index}
                        onClick={() => setAiPrompt(`Create a ${quickTemplate.name.toLowerCase()} ${nodeType} template for B2B sales. ${quickTemplate.desc}.`)}
                        className="p-3 bg-neutral-800/30 hover:bg-neutral-800/50 border border-neutral-700/50 rounded-lg text-left transition-colors"
                      >
                        <div className="text-white font-medium text-sm">{quickTemplate.name}</div>
                        <div className="text-neutral-400 text-xs">{quickTemplate.desc}</div>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </motion.div>
      </div>

      {/* Personalization Modal */}
      <PersonalizationModal
        isOpen={showPersonalizationModal}
        onClose={() => setShowPersonalizationModal(false)}
        onSave={handlePersonalizationSave}
        initialData={recipientData}
      />

      {/* Template Gallery */}
      <TemplateGallery
        isOpen={showTemplateGallery}
        onClose={() => setShowTemplateGallery(false)}
        onSelectTemplate={handleTemplateSelection}
        recipientData={recipientData}
        showPersonalization={!!recipientData}
      />
    </>
  );
};

// Node types
const nodeTypes = {
  emailNode: EmailNode,
  smsNode: SMSNode,
  waitNode: WaitNode,
  conditionNode: ConditionNode,
  linkedinNode: LinkedInNode,
  callNode: CallNode,
  triggerNode: TriggerNode
};

// Custom Node Components
function EmailNode({ data, selected }: { data: any; selected: boolean }) {
  return (
    <motion.div
      whileHover={{ scale: 1.02 }}
      className={cn(
        "relative px-4 py-3 bg-gradient-to-br from-blue-500/20 to-cyan-500/20 border border-blue-500/50 rounded-xl shadow-lg backdrop-blur-sm min-w-[250px]",
        selected && "ring-2 ring-blue-400"
      )}
    >
      <Handle type="target" position={Position.Top} className="w-3 h-3 !bg-blue-400" />
      
      <div className="flex items-start space-x-3">
        <div className="w-8 h-8 bg-blue-500 rounded-lg flex items-center justify-center">
          <Mail className="w-4 h-4 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-white text-sm">{data.label || 'Email Step'}</h3>
          <p className="text-blue-200 text-xs mt-1 line-clamp-2">
            {data.summary || 'Click to configure email content'}
          </p>
          {data.metrics && (
            <div className="flex items-center space-x-3 mt-2 text-xs">
              <span className="text-green-400">Open: {data.metrics.openRate}%</span>
              <span className="text-purple-400">Reply: {data.metrics.replyRate}%</span>
            </div>
          )}
        </div>
      </div>
      
      <Handle type="source" position={Position.Bottom} className="w-3 h-3 !bg-blue-400" />
    </motion.div>
  );
}

function SMSNode({ data, selected }: { data: any; selected: boolean }) {
  return (
    <motion.div
      whileHover={{ scale: 1.02 }}
      className={cn(
        "relative px-4 py-3 bg-gradient-to-br from-green-500/20 to-emerald-500/20 border border-green-500/50 rounded-xl shadow-lg backdrop-blur-sm min-w-[250px]",
        selected && "ring-2 ring-green-400"
      )}
    >
      <Handle type="target" position={Position.Top} className="w-3 h-3 !bg-green-400" />
      
      <div className="flex items-start space-x-3">
        <div className="w-8 h-8 bg-green-500 rounded-lg flex items-center justify-center">
          <MessageSquare className="w-4 h-4 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-white text-sm">{data.label || 'SMS Step'}</h3>
          <p className="text-green-200 text-xs mt-1 line-clamp-2">
            {data.summary || 'Click to configure SMS message'}
          </p>
          {data.metrics && (
            <div className="flex items-center space-x-3 mt-2 text-xs">
              <span className="text-green-400">Sent: {data.metrics.sent}</span>
              <span className="text-purple-400">Response: {data.metrics.responseRate}%</span>
            </div>
          )}
        </div>
      </div>
      
      <Handle type="source" position={Position.Bottom} className="w-3 h-3 !bg-green-400" />
    </motion.div>
  );
}

function WaitNode({ data, selected }: { data: any; selected: boolean }) {
  return (
    <motion.div
      whileHover={{ scale: 1.02 }}
      className={cn(
        "relative px-4 py-3 bg-gradient-to-br from-orange-500/20 to-yellow-500/20 border border-orange-500/50 rounded-xl shadow-lg backdrop-blur-sm min-w-[200px]",
        selected && "ring-2 ring-orange-400"
      )}
    >
      <Handle type="target" position={Position.Top} className="w-3 h-3 !bg-orange-400" />
      
      <div className="flex items-start space-x-3">
        <div className="w-8 h-8 bg-orange-500 rounded-lg flex items-center justify-center">
          <Clock className="w-4 h-4 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-white text-sm">{data.label || 'Wait Step'}</h3>
          <p className="text-orange-200 text-xs mt-1">
            {data.summary || 'Wait before next action'}
          </p>
        </div>
      </div>
      
      <Handle type="source" position={Position.Bottom} className="w-3 h-3 !bg-orange-400" />
    </motion.div>
  );
}

function ConditionNode({ data, selected }: { data: any; selected: boolean }) {
  return (
    <motion.div
      whileHover={{ scale: 1.02 }}
      className={cn(
        "relative px-4 py-3 bg-gradient-to-br from-purple-500/20 to-pink-500/20 border border-purple-500/50 rounded-xl shadow-lg backdrop-blur-sm min-w-[250px]",
        selected && "ring-2 ring-purple-400"
      )}
    >
      <Handle type="target" position={Position.Top} className="w-3 h-3 !bg-purple-400" />
      
      <div className="flex items-start space-x-3">
        <div className="w-8 h-8 bg-purple-500 rounded-lg flex items-center justify-center">
          <Filter className="w-4 h-4 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-white text-sm">{data.label || 'Condition'}</h3>
          <p className="text-purple-200 text-xs mt-1 line-clamp-2">
            {data.summary || 'Split based on conditions'}
          </p>
        </div>
      </div>
      
      <Handle type="source" position={Position.Left} className="w-3 h-3 !bg-green-400" style={{ top: '70%' }} />
      <Handle type="source" position={Position.Right} className="w-3 h-3 !bg-red-400" style={{ top: '70%' }} />
      <Handle type="source" position={Position.Bottom} className="w-3 h-3 !bg-purple-400" />
    </motion.div>
  );
}

function LinkedInNode({ data, selected }: { data: any; selected: boolean }) {
  return (
    <motion.div
      whileHover={{ scale: 1.02 }}
      className={cn(
        "relative px-4 py-3 bg-gradient-to-br from-blue-600/20 to-indigo-600/20 border border-blue-600/50 rounded-xl shadow-lg backdrop-blur-sm min-w-[250px]",
        selected && "ring-2 ring-blue-600"
      )}
    >
      <Handle type="target" position={Position.Top} className="w-3 h-3 !bg-blue-600" />
      
      <div className="flex items-start space-x-3">
        <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
          <Building className="w-4 h-4 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-white text-sm">{data.label || 'LinkedIn Message'}</h3>
          <p className="text-blue-200 text-xs mt-1 line-clamp-2">
            {data.summary || 'Send LinkedIn connection/message'}
          </p>
        </div>
      </div>
      
      <Handle type="source" position={Position.Bottom} className="w-3 h-3 !bg-blue-600" />
    </motion.div>
  );
}

function CallNode({ data, selected }: { data: any; selected: boolean }) {
  return (
    <motion.div
      whileHover={{ scale: 1.02 }}
      className={cn(
        "relative px-4 py-3 bg-gradient-to-br from-red-500/20 to-pink-500/20 border border-red-500/50 rounded-xl shadow-lg backdrop-blur-sm min-w-[250px]",
        selected && "ring-2 ring-red-400"
      )}
    >
      <Handle type="target" position={Position.Top} className="w-3 h-3 !bg-red-400" />
      
      <div className="flex items-start space-x-3">
        <div className="w-8 h-8 bg-red-500 rounded-lg flex items-center justify-center">
          <Phone className="w-4 h-4 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-white text-sm">{data.label || 'Call Task'}</h3>
          <p className="text-red-200 text-xs mt-1 line-clamp-2">
            {data.summary || 'Schedule or make a call'}
          </p>
        </div>
      </div>
      
      <Handle type="source" position={Position.Bottom} className="w-3 h-3 !bg-red-400" />
    </motion.div>
  );
}

function TriggerNode({ data, selected }: { data: any; selected: boolean }) {
  return (
    <motion.div
      whileHover={{ scale: 1.02 }}
      className={cn(
        "relative px-4 py-3 bg-gradient-to-br from-emerald-500/20 to-teal-500/20 border border-emerald-500/50 rounded-xl shadow-lg backdrop-blur-sm min-w-[200px]",
        selected && "ring-2 ring-emerald-400"
      )}
    >
      <div className="flex items-start space-x-3">
        <div className="w-8 h-8 bg-emerald-500 rounded-lg flex items-center justify-center">
          <Zap className="w-4 h-4 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-white text-sm">{data.label || 'Trigger'}</h3>
          <p className="text-emerald-200 text-xs mt-1">
            {data.summary || 'Sequence starts here'}
          </p>
        </div>
      </div>
      
      <Handle type="source" position={Position.Bottom} className="w-3 h-3 !bg-emerald-400" />
    </motion.div>
  );
}

const SequenceBuilderPage: React.FC = () => {
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [selectedNode, setSelectedNode] = useState<Node | null>(null);
  const [showProperties, setShowProperties] = useState(false);
  const [showAIPanel, setShowAIPanel] = useState(true);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [templateModalType, setTemplateModalType] = useState<'email' | 'sms' | 'linkedin' | 'call'>('email');
  const [aiInput, setAiInput] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [sequenceName, setSequenceName] = useState('Untitled Sequence');
  const [sequenceDescription, setSequenceDescription] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [existingTemplates, setExistingTemplates] = useState<Template[]>([]);
  const reactFlowWrapper = useRef<HTMLDivElement>(null);

  // Load existing templates
  useEffect(() => {
    loadExistingTemplates();
  }, []);

  const loadExistingTemplates = async () => {
    // Mock existing templates - in real app, load from API
    const mockTemplates: Template[] = [
      {
        id: 'tpl-001',
        name: 'Cold Outreach - SaaS',
        category: 'cold-outreach',
        type: 'email',
        subject: 'Quick question about {{company}} growth',
        content: 'Hi {{firstName}}, I noticed {{company}} has been expanding rapidly. I wanted to reach out because we help similar companies...',
        tags: ['saas', 'cold', 'growth'],
        openRate: 24.5,
        replyRate: 8.2,
        usage: 156,
        createdAt: '2024-01-15',
        updatedAt: '2024-01-20',
        isActive: true,
        author: 'Sales Team'
      },
      {
        id: 'tpl-002',
        name: 'Follow-up Email',
        category: 'follow-up',
        type: 'email',
        subject: 'Following up on our conversation',
        content: 'Hi {{firstName}}, Thanks for taking the time to speak with me yesterday about {{topic}}...',
        tags: ['follow-up', 'conversation'],
        openRate: 45.2,
        replyRate: 15.8,
        usage: 89,
        createdAt: '2024-01-18',
        updatedAt: '2024-01-22',
        isActive: true,
        author: 'Sales Team'
      },
      {
        id: 'tpl-003',
        name: 'LinkedIn Connection Request',
        category: 'cold-outreach',
        type: 'linkedin',
        content: 'Hi {{firstName}}, I noticed you\'re the {{title}} at {{company}}. I\'d love to connect and share some insights about {{industry}} trends.',
        tags: ['linkedin', 'connection', 'industry'],
        usage: 234,
        createdAt: '2024-01-10',
        updatedAt: '2024-01-25',
        isActive: true,
        author: 'Marketing Team'
      }
    ];
    setExistingTemplates(mockTemplates);
  };

  const onConnect = useCallback(
    (params: Connection) => setEdges((eds) => addEdge({
      ...params,
      type: 'smoothstep',
      markerEnd: { type: MarkerType.ArrowClosed, color: '#6366f1' }
    }, eds)),
    [setEdges]
  );

  const onNodeClick = (event: React.MouseEvent, node: Node) => {
    setSelectedNode(node);
    
    // Open template modal for content nodes
    if (['emailNode', 'smsNode', 'linkedinNode', 'callNode'].includes(node.type || '')) {
      const nodeType = node.type?.replace('Node', '') as 'email' | 'sms' | 'linkedin' | 'call';
      setTemplateModalType(nodeType);
      setShowTemplateModal(true);
    } else {
      setShowProperties(true);
    }
  };

  // Initialize with a trigger node
  useEffect(() => {
    if (nodes.length === 0) {
      const triggerNode: Node = {
        id: 'trigger-1',
        type: 'triggerNode',
        position: { x: 250, y: 100 },
        data: {
          label: 'Sequence Start',
          summary: 'New contact enters sequence'
        }
      };
      setNodes([triggerNode]);
    }
  }, []);

  const generateSequenceFromAI = async () => {
    if (!aiInput.trim()) return;

    setIsGenerating(true);
    try {
      const request: SequenceRequest = {
        description: aiInput,
        objective: 'Generate qualified leads and meetings',
        targetAudience: 'B2B professionals',
        industry: 'Technology',
        channels: ['email', 'linkedin'],
        timeline: '2-3 weeks'
      };

      const generatedSequence = await GeminiService.generateSequence(request);
      
      setSequenceName(generatedSequence.name);
      setSequenceDescription(generatedSequence.description);
      setNodes(generatedSequence.nodes);
      setEdges(generatedSequence.edges);
      setAiInput('');
      setShowAIPanel(false);
    } catch (error) {
      console.error('AI generation failed:', error);
      // Fallback to mock generation
      const generatedNodes = parseAIInput(aiInput);
      const generatedEdges = connectNodes(generatedNodes);
      setNodes(generatedNodes);
      setEdges(generatedEdges);
      setAiInput('');
      setShowAIPanel(false);
    } finally {
      setIsGenerating(false);
    }
  };

  const parseAIInput = (input: string): Node[] => {
    // This would integrate with a real LLM API
    // For demo, creating a comprehensive sequence based on common patterns

    const baseNodes: Node[] = [
      {
        id: 'trigger-1',
        type: 'triggerNode',
        position: { x: 400, y: 50 },
        data: {
          label: 'Sequence Start',
          summary: 'New lead enters sequence'
        }
      }
    ];

    // Analyze input for different sequence types
    if (input.toLowerCase().includes('sales') || input.toLowerCase().includes('outreach')) {
      return [
        ...baseNodes,
        {
          id: 'email-1',
          type: 'emailNode',
          position: { x: 400, y: 150 },
          data: {
            label: 'Initial Outreach',
            summary: 'Personalized introduction email highlighting value proposition',
            metrics: { openRate: 28, replyRate: 4.2 },
            template: {
              subject: 'Quick question about {{company}} growth',
              content: 'Hi {{firstName}}, I noticed {{company}} is expanding rapidly...'
            }
          }
        },
        {
          id: 'wait-1',
          type: 'waitNode',
          position: { x: 400, y: 250 },
          data: {
            label: 'Wait 3 Days',
            summary: 'Wait 3 business days before follow-up'
          }
        },
        {
          id: 'condition-1',
          type: 'conditionNode',
          position: { x: 400, y: 350 },
          data: {
            label: 'Email Opened?',
            summary: 'Check if prospect opened previous email'
          }
        },
        {
          id: 'email-2',
          type: 'emailNode',
          position: { x: 200, y: 450 },
          data: {
            label: 'Follow-up (Opened)',
            summary: 'Follow-up email with case study for engaged prospects',
            metrics: { openRate: 35, replyRate: 6.8 }
          }
        },
        {
          id: 'email-3',
          type: 'emailNode',
          position: { x: 600, y: 450 },
          data: {
            label: 'Re-engagement',
            summary: 'Different angle email for non-openers',
            metrics: { openRate: 22, replyRate: 3.1 }
          }
        },
        {
          id: 'linkedin-1',
          type: 'linkedinNode',
          position: { x: 400, y: 550 },
          data: {
            label: 'LinkedIn Connection',
            summary: 'Send LinkedIn connection request with personalized note'
          }
        },
        {
          id: 'wait-2',
          type: 'waitNode',
          position: { x: 400, y: 650 },
          data: {
            label: 'Wait 5 Days',
            summary: 'Wait 5 business days for connection acceptance'
          }
        },
        {
          id: 'call-1',
          type: 'callNode',
          position: { x: 400, y: 750 },
          data: {
            label: 'Phone Call',
            summary: 'Schedule discovery call to discuss needs'
          }
        }
      ];
    }

    // Default nurture sequence
    return [
      ...baseNodes,
      {
        id: 'email-1',
        type: 'emailNode',
        position: { x: 400, y: 150 },
        data: {
          label: 'Welcome Email',
          summary: 'Warm welcome message with helpful resources',
          metrics: { openRate: 45, replyRate: 8.3 }
        }
      },
      {
        id: 'wait-1',
        type: 'waitNode',
        position: { x: 400, y: 250 },
        data: {
          label: 'Wait 2 Days',
          summary: 'Wait 2 days before sending educational content'
        }
      },
      {
        id: 'email-2',
        type: 'emailNode',
        position: { x: 400, y: 350 },
        data: {
          label: 'Educational Content',
          summary: 'Share valuable industry insights and tips',
          metrics: { openRate: 38, replyRate: 5.7 }
        }
      }
    ];
  };

  const connectNodes = (nodes: Node[]): Edge[] => {
    const edges: Edge[] = [];
    for (let i = 0; i < nodes.length - 1; i++) {
      edges.push({
        id: `edge-${i}`,
        source: nodes[i].id,
        target: nodes[i + 1].id,
        type: 'smoothstep',
        markerEnd: { type: MarkerType.ArrowClosed, color: '#6366f1' }
      });
    }
    return edges;
  };

  const addNode = (type: string) => {
    const newNode: Node = {
      id: `${type}-${Date.now()}`,
      type: `${type}Node`,
      position: { x: 400, y: 400 },
      data: {
        label: type.charAt(0).toUpperCase() + type.slice(1) + ' Step',
        summary: `Configure your ${type} step`
      }
    };
    setNodes((nds) => [...nds, newNode]);
  };

  const handleTemplateSelection = (template: Template) => {
    if (selectedNode) {
      const updatedNode = {
        ...selectedNode,
        data: {
          ...selectedNode.data,
          label: template.name,
          summary: template.content.substring(0, 100) + '...',
          template: {
            id: template.id,
            name: template.name,
            subject: template.subject,
            content: template.content
          },
          metrics: {
            openRate: template.openRate,
            replyRate: template.replyRate
          }
        }
      };
      
      setNodes((nds) => nds.map((node) => node.id === selectedNode.id ? updatedNode : node));
    }
  };

  const handleTemplateCreation = (generatedTemplate: GeneratedTemplate) => {
    // Add to existing templates
    const newTemplate: Template = {
      id: `tpl-${Date.now()}`,
      name: generatedTemplate.name,
      category: generatedTemplate.category,
      type: templateModalType,
      subject: generatedTemplate.subject,
      content: generatedTemplate.content,
      tags: generatedTemplate.tags,
      usage: 0,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      isActive: true,
      author: 'AI Generated'
    };
    
    setExistingTemplates(prev => [...prev, newTemplate]);
    
    // Apply to selected node
    if (selectedNode) {
      const updatedNode = {
        ...selectedNode,
        data: {
          ...selectedNode.data,
          label: generatedTemplate.name,
          summary: generatedTemplate.content.substring(0, 100) + '...',
          template: {
            id: newTemplate.id,
            name: generatedTemplate.name,
            subject: generatedTemplate.subject,
            content: generatedTemplate.content
          }
        }
      };
      
      setNodes((nds) => nds.map((node) => node.id === selectedNode.id ? updatedNode : node));
    }
  };

  const saveSequence = async () => {
    setIsSaving(true);
    try {
      const sequence: Sequence = {
        id: `seq-${Date.now()}`,
        name: sequenceName,
        description: sequenceDescription,
        nodes,
        edges,
        status: 'draft',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        createdBy: 'Current User',
        estimatedDuration: '2-3 weeks',
        expectedResults: {
          estimatedReach: 1000,
          expectedOpenRate: 25,
          expectedReplyRate: 8,
          estimatedMeetings: 80
        }
      };

      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      console.log('Sequence saved:', sequence);
      
      // Show success message (you could add a toast notification here)
      alert('Sequence saved successfully!');
    } catch (error) {
      console.error('Failed to save sequence:', error);
      alert('Failed to save sequence. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="h-screen bg-gradient-to-br from-neutral-950 via-neutral-900 to-neutral-950 flex">
      <ReactFlowProvider>
        {/* AI Panel */}
        <AnimatePresence>
          {showAIPanel && (
            <motion.div
              initial={{ x: -400, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: -400, opacity: 0 }}
              className="w-96 bg-gradient-to-br from-neutral-900/95 to-neutral-800/95 backdrop-blur-xl border-r border-neutral-700/50 p-6 overflow-y-auto"
            >
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-2">
                  <div className="w-8 h-8 bg-gradient-to-r from-purple-500 to-pink-500 rounded-lg flex items-center justify-center">
                    <Brain className="w-4 h-4 text-white" />
                  </div>
                  <h2 className="text-xl font-bold text-white">AI Sequence Builder</h2>
                </div>
                <button
                  onClick={() => setShowAIPanel(false)}
                  className="text-neutral-400 hover:text-white"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="space-y-4 mb-6">
                <div>
                  <label className="block text-sm font-medium text-neutral-300 mb-2">
                    Describe your sequence
                  </label>
                  <textarea
                    value={aiInput}
                    onChange={(e) => setAiInput(e.target.value)}
                    placeholder="I want to create a sales outreach sequence for B2B SaaS leads. Start with a personalized email, wait 3 days, then follow up based on engagement. Include LinkedIn outreach and a phone call."
                    rows={6}
                    className="w-full px-4 py-3 bg-neutral-800/50 border border-neutral-700 rounded-xl text-white placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-transparent resize-none"
                  />
                </div>

                <Button
                  onClick={generateSequenceFromAI}
                  disabled={!aiInput.trim() || isGenerating}
                  className="w-full bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600"
                >
                  {isGenerating ? (
                    <>
                      <Sparkles className="w-4 h-4 mr-2 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Wand2 className="w-4 h-4 mr-2" />
                      Generate Sequence
                    </>
                  )}
                </Button>
              </div>

              <div className="space-y-3">
                <h3 className="text-sm font-medium text-neutral-300 mb-3">Quick Templates</h3>
                {[
                  { name: 'B2B Sales Outreach', desc: 'Multi-touch sequence for B2B leads' },
                  { name: 'Customer Onboarding', desc: 'Welcome series for new customers' },
                  { name: 'Lead Nurturing', desc: 'Educational content sequence' },
                  { name: 'Re-engagement', desc: 'Win back inactive contacts' }
                ].map((template, index) => (
                  <button
                    key={index}
                    onClick={() => setAiInput(`Create a ${template.name.toLowerCase()} sequence. ${template.desc}.`)}
                    className="w-full p-3 bg-neutral-800/30 hover:bg-neutral-800/50 border border-neutral-700/50 rounded-lg text-left transition-colors"
                  >
                    <div className="text-white font-medium text-sm">{template.name}</div>
                    <div className="text-neutral-400 text-xs">{template.desc}</div>
                  </button>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Main Canvas */}
        <div className="flex-1 flex flex-col">
          {/* Header */}
          <div className="h-16 bg-gradient-to-r from-neutral-900/95 to-neutral-800/95 backdrop-blur-xl border-b border-neutral-700/50 flex items-center justify-between px-6">
            <div className="flex items-center space-x-4">
              <input
                value={sequenceName}
                onChange={(e) => setSequenceName(e.target.value)}
                className="text-xl font-bold text-white bg-transparent border-none outline-none"
              />
              <div className="flex items-center space-x-2">
                <span className="text-neutral-400 text-sm">•</span>
                <span className="text-neutral-400 text-sm">{nodes.length} steps</span>
              </div>
            </div>

            <div className="flex items-center space-x-3">
              {!showAIPanel && (
                <Button
                  onClick={() => setShowAIPanel(true)}
                  variant="outline"
                  size="sm"
                >
                  <Brain className="w-4 h-4 mr-2" />
                  AI Assistant
                </Button>
              )}
              <Button variant="outline" size="sm">
                <Eye className="w-4 h-4 mr-2" />
                Preview
              </Button>
              <Button 
                size="sm" 
                onClick={saveSequence}
                disabled={isSaving}
                className="bg-gradient-to-r from-blue-500 to-purple-600"
              >
                {isSaving ? (
                  <>
                    <Upload className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Save Sequence
                  </>
                )}
              </Button>
            </div>
          </div>

          {/* Canvas */}
          <div className="flex-1 relative" ref={reactFlowWrapper}>
            <ReactFlow
              nodes={nodes}
              edges={edges}
              onNodesChange={onNodesChange}
              onEdgesChange={onEdgesChange}
              onConnect={onConnect}
              onNodeClick={onNodeClick}
              nodeTypes={nodeTypes}
              fitView
              className="bg-gradient-to-br from-neutral-950 to-neutral-900"
            >
              <Background color="#374151" gap={20} />
              <Controls className="bg-neutral-800 border-neutral-700" />
              <MiniMap 
                className="bg-neutral-800 border-neutral-700"
                nodeColor="#6366f1"
                maskColor="rgba(0, 0, 0, 0.8)"
              />
            </ReactFlow>

            {/* Floating Toolbar */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="absolute bottom-6 left-1/2 transform -translate-x-1/2"
            >
              <div className="flex items-center space-x-2 bg-gradient-to-r from-neutral-900/95 to-neutral-800/95 backdrop-blur-xl border border-neutral-700/50 rounded-2xl p-3 shadow-2xl">
                <Button
                  onClick={() => addNode('email')}
                  size="sm"
                  variant="outline"
                  className="border-blue-500/50 text-blue-400 hover:bg-blue-500/10"
                >
                  <Mail className="w-4 h-4 mr-2" />
                  Email
                </Button>
                <Button
                  onClick={() => addNode('sms')}
                  size="sm"
                  variant="outline"
                  className="border-green-500/50 text-green-400 hover:bg-green-500/10"
                >
                  <MessageSquare className="w-4 h-4 mr-2" />
                  SMS
                </Button>
                <Button
                  onClick={() => addNode('wait')}
                  size="sm"
                  variant="outline"
                  className="border-orange-500/50 text-orange-400 hover:bg-orange-500/10"
                >
                  <Clock className="w-4 h-4 mr-2" />
                  Wait
                </Button>
                <Button
                  onClick={() => addNode('condition')}
                  size="sm"
                  variant="outline"
                  className="border-purple-500/50 text-purple-400 hover:bg-purple-500/10"
                >
                  <Filter className="w-4 h-4 mr-2" />
                  Condition
                </Button>
                <Button
                  onClick={() => addNode('linkedin')}
                  size="sm"
                  variant="outline"
                  className="border-blue-600/50 text-blue-300 hover:bg-blue-600/10"
                >
                  <Building className="w-4 h-4 mr-2" />
                  LinkedIn
                </Button>
                <Button
                  onClick={() => addNode('call')}
                  size="sm"
                  variant="outline"
                  className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                >
                  <Phone className="w-4 h-4 mr-2" />
                  Call
                </Button>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Template Selection Modal */}
        <AnimatePresence>
          {showTemplateModal && (
            <TemplateSelectionModal
              isOpen={showTemplateModal}
              onClose={() => setShowTemplateModal(false)}
              nodeType={templateModalType}
              existingTemplates={existingTemplates}
              onSelectTemplate={handleTemplateSelection}
              onCreateTemplate={handleTemplateCreation}
              selectedNode={selectedNode}
            />
          )}
        </AnimatePresence>

        {/* Properties Panel */}
        <AnimatePresence>
          {showProperties && selectedNode && (
            <motion.div
              initial={{ x: 400, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: 400, opacity: 0 }}
              className="w-96 bg-gradient-to-br from-neutral-900/95 to-neutral-800/95 backdrop-blur-xl border-l border-neutral-700/50 p-6 overflow-y-auto"
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-white">Step Properties</h2>
                <button
                  onClick={() => setShowProperties(false)}
                  className="text-neutral-400 hover:text-white"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-neutral-300 mb-2">
                    Step Name
                  </label>
                  <input
                    value={selectedNode.data.label}
                    onChange={(e) => {
                      const updatedNode = {
                        ...selectedNode,
                        data: { ...selectedNode.data, label: e.target.value }
                      };
                      setNodes((nds) =>
                        nds.map((node) =>
                          node.id === selectedNode.id ? updatedNode : node
                        )
                      );
                      setSelectedNode(updatedNode);
                    }}
                    className="w-full px-4 py-3 bg-neutral-800/50 border border-neutral-700 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50"
                  />
                </div>

                {selectedNode.type === 'emailNode' && (
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-neutral-300 mb-2">
                        Subject Line
                      </label>
                      <input
                        placeholder="Enter email subject..."
                        className="w-full px-4 py-3 bg-neutral-800/50 border border-neutral-700 rounded-xl text-white placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-neutral-300 mb-2">
                        Email Content
                      </label>
                      <textarea
                        rows={6}
                        placeholder="Hi {{firstName}}, &#10;&#10;I noticed {{company}} is..."
                        className="w-full px-4 py-3 bg-neutral-800/50 border border-neutral-700 rounded-xl text-white placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50 resize-none"
                      />
                    </div>
                  </div>
                )}

                {selectedNode.type === 'waitNode' && (
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-neutral-300 mb-2">
                        Wait Duration
                      </label>
                      <div className="flex space-x-3">
                        <input
                          type="number"
                          placeholder="3"
                          className="flex-1 px-4 py-3 bg-neutral-800/50 border border-neutral-700 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-orange-500/50"
                        />
                        <select className="px-4 py-3 bg-neutral-800/50 border border-neutral-700 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-orange-500/50">
                          <option>Days</option>
                          <option>Hours</option>
                          <option>Minutes</option>
                        </select>
                      </div>
                    </div>
                  </div>
                )}

                <div className="pt-4 border-t border-neutral-700">
                  <h3 className="text-sm font-medium text-neutral-300 mb-3">Advanced Settings</h3>
                  <div className="space-y-3">
                    <label className="flex items-center space-x-3">
                      <input type="checkbox" className="rounded border-neutral-600 bg-neutral-800" />
                      <span className="text-sm text-neutral-400">Respect business hours</span>
                    </label>
                    <label className="flex items-center space-x-3">
                      <input type="checkbox" className="rounded border-neutral-600 bg-neutral-800" />
                      <span className="text-sm text-neutral-400">Skip weekends</span>
                    </label>
                    <label className="flex items-center space-x-3">
                      <input type="checkbox" className="rounded border-neutral-600 bg-neutral-800" />
                      <span className="text-sm text-neutral-400">A/B test this step</span>
                    </label>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </ReactFlowProvider>
    </div>
  );
};

export default SequenceBuilderPage; 